import { Send } from '@mui/icons-material';
import {
  Alert,
  Container,
  Drawer,
  IconButton,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ChatBubbleComponent from '../components/Chat/ChatBubble';
import ChatHistory from '../components/Chat/ChatHistory';
import ChatInfo from '../components/Chat/ChatInfo';
import FunctionSettings from '../components/Chat/FunctionSettings';
import LimitWarning from '../components/Chat/LimitWarning';
import DrawerToggleButton from '../components/DrawerToggleButton';
import { MessageFrom } from '../enums';
import {
  getConversation,
  getConversations,
  getMessages,
  postConversation,
  streamMessage,
  updateConversation,
} from '../services/conversation';
import { findPrompt } from '../services/prompts';
import { Conversation, Message } from '../Types/conversation';
import { FunctionName } from '../Types/enums';
import { findLlmModel } from '../Types/llmModels';
import { Prompt } from '../Types/prompt';

const ChatPageContainer = styled('div')({
  display: 'flex',
  height: 'inherit',

  div: {
    position: 'relative',
  },
});

type ContainerProps = {
  open: boolean;
};

const drawerWidth = '350px';

const DrawerContainer = styled('div')<ContainerProps>(({ open }) => ({
  width: open ? drawerWidth : 'auto',
  zIndex: '1',
}));

const MainContainer = styled('div')<ContainerProps>(({ open }) => ({
  marginLeft: open ? 'auto' : `-${drawerWidth}`,
  width: `100%`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  zIndex: 1,
  alignItems: 'flex-start',
  height: 'inherit',
}));

const StyledChatContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  marginLeft: 'auto',
  marginRight: 'auto',
  justifyContent: 'space-between',
  minHeight: '100%',

  '& > *:not(:last-child)': {
    textAlign: 'center',
  },
});

// Used by the commented-out disclaimer banner below
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StyledAlert = styled(Alert)({
  marginBottom: '10px',
  marginTop: '15px',
  backgroundColor: '#B4B4B4',
  color: 'white',
  '.MuiAlert-icon': { display: 'flex', alignItems: 'center' },
});

const ChatContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});

const ChatRow = styled('div')({
  display: 'flex',
  gap: '5px',
  marginBottom: '16px',
  alignItems: 'flex-end',
  position: 'sticky',
  bottom: '0',
  background: '#fff',
  paddingTop: '10px',
});

const SendButton = styled(IconButton)({
  height: '40px',
  width: '40px',
});

export type ChatProps = {
  llm: string;
};

const Chat = ({ llm }: ChatProps): JSX.Element => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [shouldFetchMessages, setShouldFetchMessages] = useState<boolean>(false);
  const [shouldFetchConversations, setShouldFetchConversations] = useState<boolean>(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoadingResponse, setIsLoadingResponse] = useState<boolean>(false);
  // AI response text received so far while it streams in
  const [streamingText, setStreamingText] = useState<string>('');
  // Identifies the in-flight stream, so a reply for a conversation the user has
  // since left is ignored (the server still saves it)
  const activeStreamRef = useRef<symbol>();
  // Whether new streamed text should keep the view scrolled to the bottom
  const followStreamRef = useRef<boolean>(true);
  const { conversationId } = useParams();
  const [currentConversationId, setCurrentConversationId] = useState<string>();
  const [currentConversation, setCurrentConversation] = useState<Conversation>();
  const messageContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>();
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const [promptId, setPromptId] = useState<string>();
  const [prompt, setPrompt] = useState<Prompt>();
  const [showErrorMessage, setShowErrorMessage] = useState('');
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState<boolean>(
    window.matchMedia('(min-width: 800px)').matches
  );
  const [functions, setFunctions] = useState<FunctionName[]>([]);
  const [useFunctions, setUseFunctions] = useState<boolean>(false);
  const [tokens, setTokens] = useState<number>(0);
  const [showChatInfo, setShowChatInfo] = useState<boolean>(false);
  const [greyAllMessages, setGreyAllMessages] = useState<boolean>(false);

  useEffect(() => {
    function handleResize() {
      const width = window.innerWidth;

      if (width < 1200) {
        setHistoryDrawerOpen(false);
      } else {
        setHistoryDrawerOpen(true);
      }
    }
    window.addEventListener('resize', handleResize);
  });

  // These three functions are hacks to get around costly rerenders for this page
  // this monolithic page should be refactored into smaller pieces
  const setChatPrompt = (prompt: string) => {
    if (inputRef.current) {
      inputRef.current.value = prompt;
    }
  };

  const getChatPrompt = () => {
    if (inputRef.current) {
      return inputRef.current.value;
    }
    return '';
  };

  const handleSubmit = useCallback(
    async (prompt?: Prompt) => {
      if (inputRef.current) {
        const ref = inputRef.current;

        if (ref.value === '') {
          setShowErrorMessage('Please enter your message!');
          setTimeout(() => {
            setShowErrorMessage('');
          }, 5000);
          return;
        }

        setIsLoadingResponse(true);
        setStreamingText('');
        const message = getChatPrompt();
        const stream = Symbol();
        activeStreamRef.current = stream;
        const isActiveStream = () => activeStreamRef.current === stream;
        const onDelta = (delta: string) => {
          if (isActiveStream()) setStreamingText((text) => text + delta);
        };

        ref.value = '';

        let newConversationTitle;
        if (!conversationId) {
          if (prompt) {
            newConversationTitle = prompt.title;
          } else {
            newConversationTitle = message;
          }

          const newConversation = await postConversation({
            title: newConversationTitle,
            promptId: searchParams.get('prompt') ?? undefined,
            functions: [...functions, ...(prompt?.functions ?? [])],
          });

          if (!prompt && message) {
            setMessages([
              ...messages,
              {
                conversationId: '',
                message,
                from: MessageFrom.USER,
                createdAt: '',
                id: '',
                deleted: false,
                tokens: 0,
              },
            ]);
          }

          await streamMessage(
            { conversationId: newConversation.id, message: message },
            llm,
            onDelta
          );

          setShouldFetchConversations(true);
          if (!isActiveStream()) return;
          activeStreamRef.current = undefined;
          if (window.location.pathname === '/chat') {
            navigate(`/chat/${newConversation.id}`);
          }
        } else {
          setGreyAllMessages(true);

          setMessages([
            ...messages,
            {
              conversationId,
              message,
              from: MessageFrom.USER,
              createdAt: '',
              id: '',
              deleted: false,
              tokens: 0,
            },
          ]);

          await streamMessage({ conversationId, message: message }, llm, onDelta);

          if (!isActiveStream()) return;
          activeStreamRef.current = undefined;
          setShouldFetchMessages(true);
        }
      }
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    [conversationId, searchParams, functions, llm, messages, navigate]
  );

  // Triggers when a new prompt is selected
  // updates promptId
  useEffect(() => {
    const newPromptId = searchParams.get('prompt');
    if (newPromptId) {
      setPromptId(newPromptId);
    }
  }, [searchParams]);

  //Triggers when the user triggers a submit, changes the navigation, changes the prompt, or changes the promptId
  //fetches the prompt, sets prompt and functions
  useEffect(() => {
    if (promptId && !prompt) {
      const fetchPrompt = async () => {
        const prompt = await findPrompt(promptId);

        setPrompt(prompt);
        setChatPrompt(prompt.prompt);
        setUseFunctions(prompt.functions && prompt.functions.length > 0 ? true : false);
        setFunctions(prompt.functions ?? []);

        if (!prompt.userEditRequired) {
          await handleSubmit(prompt);
        }
      };
      fetchPrompt();
    }
  }, [handleSubmit, navigate, prompt, promptId]);

  useEffect(() => {
    if (currentConversationId && currentConversation && shouldFetchMessages) {
      const fetchMesages = async () => {
        try {
          const newMessages = await getMessages(currentConversationId);
          if (
            currentConversation?.prompt &&
            currentConversation.prompt.userEditRequired === false
          ) {
            if (newMessages.length > 0) {
              newMessages.shift();
            }
          }
          setMessages(newMessages);
          // set tokens based on the most recent message in the conversation where the ai is the sender
          const mostRecentAIMessages = newMessages.filter(
            (message) => message.from === MessageFrom.AI
          );
          setTokens(
            mostRecentAIMessages.length > 0
              ? mostRecentAIMessages[mostRecentAIMessages.length - 1].tokens
              : 0
          );
          setIsLoadingResponse(false);
          setStreamingText('');
          setShouldFetchMessages(false);
        } catch {
          enqueueSnackbar('Error fetching messages', { variant: 'error' });
          navigate('/chat');
        }
      };
      fetchMesages();
    }
  }, [currentConversation, currentConversationId, enqueueSnackbar, navigate, shouldFetchMessages]);

  useEffect(() => {
    if (currentConversationId !== conversationId) {
      const fetchConversation = async () => {
        if (conversationId) {
          const newConversation = await getConversation(conversationId);
          setCurrentConversationId(conversationId);
          setCurrentConversation(newConversation);

          let newFunctions = newConversation.functions ?? [];
          setFunctions(newFunctions);
          newFunctions = Array.from(
            new Set(newFunctions.concat(newConversation?.prompt?.functions ?? []))
          );

          setUseFunctions(newFunctions.length > 0 ? true : false);
          setShouldFetchMessages(true);
        } else {
          setCurrentConversationId(conversationId);
          setCurrentConversation(undefined);
          setFunctions([]);
          setUseFunctions(false);
        }
      };
      fetchConversation();
    }
  }, [conversationId, currentConversation, currentConversationId]);

  useEffect(() => {
    if (shouldFetchConversations) {
      const fetchConversations = async () => {
        const newConversations = await getConversations();
        setConversations(newConversations);
        setShouldFetchConversations(false);
        setGreyAllMessages(false);
      };
      fetchConversations();
    }
  }, [setShouldFetchConversations, shouldFetchConversations]);

  const handleConversationUpdate = (conversationId?: string) => {
    setPrompt(undefined);
    setPromptId(undefined);
    setShouldFetchConversations(true);
    if (conversationId) {
      navigate(`/chat/${conversationId}`);
    } else {
      navigate('/chat');
    }

    if (inputRef.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    if (currentConversationId && !conversationId) {
      setMessages([]);
      setChatPrompt('');
      setTokens(0);
    }
  }, [conversationId, currentConversationId]);

  // Leaving a conversation abandons its in-flight reply so it can't show up in,
  // or block, the next one
  useEffect(() => {
    if (activeStreamRef.current) {
      activeStreamRef.current = undefined;
      setIsLoadingResponse(false);
      setStreamingText('');
      setGreyAllMessages(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
      followStreamRef.current = true;
    }
  }, [messages, currentConversationId]);

  // Follow the streaming reply, unless the user has scrolled up to read
  useEffect(() => {
    if (streamingText && followStreamRef.current && messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  }, [streamingText]);

  const handleMessagesScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    followStreamRef.current = scrollHeight - scrollTop - clientHeight < 40;
  };

  const maxTokens = findLlmModel(llm).maxTokens;

  useEffect(() => {
    setShowChatInfo(window.location.pathname === '/chat');
  }, [navigate]);

  // Function to handle the selecting of function checkboxes
  const updateFunctions = useCallback(
    async (funcNames: FunctionName[]) => {
      setFunctions(funcNames);
      await updateConversation({ functions: funcNames }, conversationId ?? '');
    },
    [conversationId]
  );

  return (
    <ChatPageContainer>
      <DrawerContainer open={historyDrawerOpen}>
        <Drawer
          variant="persistent"
          anchor="left"
          open={historyDrawerOpen}
          sx={{ height: '100%', width: drawerWidth }}
        >
          <ChatHistory
            conversations={conversations}
            handleConversationUpdate={handleConversationUpdate}
          />
        </Drawer>
      </DrawerContainer>
      <DrawerToggleButton
        isDrawerOpen={historyDrawerOpen}
        setIsDrawerOpen={setHistoryDrawerOpen}
        tooltipCaption={historyDrawerOpen ? 'Close History Panel' : 'Open History Panel'}
      />

      <MainContainer open={historyDrawerOpen}>
        <div
          style={{ overflow: 'auto', width: '100%', minHeight: '100%' }}
          ref={messageContainerRef}
          onScroll={handleMessagesScroll}
        >
          <StyledChatContainer maxWidth="md">
            {/* <div style={{ position: 'sticky', zIndex: '999', marginBottom: '10px', top: '0' }}>
              <StyledAlert icon={`🔍`}>
                <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: '500' }}>
                    Always double check the output of Alex for inaccuracies before using it.
                  </Typography>
                </div>
              </StyledAlert>
            </div> */}
            <div
              style={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                marginBottom: '20px',
              }}
            >
              <FunctionSettings
                functions={functions}
                disabledOnFunctions={currentConversation?.prompt?.functions ?? []}
                handleFunctionsUpdate={updateFunctions}
                useFunctions={useFunctions}
                setUseFunctions={setUseFunctions}
              />
              <ChatInfo
                currentConversation={currentConversation}
                currentPrompt={prompt}
                shouldShowInfo={showChatInfo}
                useFunctions={useFunctions}
              />
            </div>
            <ChatContainer>
              {messages.length === 0 && !prompt?.id && (
                <ChatBubbleComponent
                  key={'placeholder'}
                  message={{
                    from: MessageFrom.AI,
                    message:
                      'Start a new conversation with me by typing in the box or select an old conversation from the left! 😊',
                  }}
                  loading={shouldFetchMessages}
                />
              )}
              {messages.length === 0 && prompt && prompt.userEditRequired && (
                <>
                  <ChatBubbleComponent
                    key={'placeholder'}
                    message={{
                      from: MessageFrom.AI,
                      message: `Looks like you're using a prompt, make sure to edit it if necessary before sending.`,
                    }}
                    loading={shouldFetchMessages}
                  />
                </>
              )}
              {messages.map((message) => (
                <ChatBubbleComponent
                  key={message.id}
                  message={message}
                  loading={shouldFetchConversations || (shouldFetchMessages && !greyAllMessages)}
                />
              ))}
              {isLoadingResponse && (
                <ChatBubbleComponent
                  key={'loading'}
                  message={{ from: MessageFrom.AI, message: streamingText || undefined }}
                  loading={!streamingText}
                />
              )}
              <ChatRow>
                <div style={{ paddingBottom: '20px', flexGrow: 1 }}>
                  <TextField
                    inputRef={(ref) => (inputRef.current = ref)}
                    variant="outlined"
                    label="Please enter a message"
                    InputProps={{
                      inputProps: {
                        style: { fontSize: '14px' },
                      },
                    }}
                    multiline
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!showErrorMessage}
                    helperText={showErrorMessage}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(prompt);
                      }
                    }}
                    autoComplete="off"
                  />
                  <LimitWarning tokens={tokens} maxTokens={maxTokens} />
                </div>
                <div
                  style={{
                    display: 'flex',
                    height: '100%',
                    paddingBottom: '52px',
                    boxSizing: 'border-box',
                  }}
                >
                  <Tooltip title={<Typography variant="caption"> Send </Typography>}>
                    <span>
                      <SendButton
                        onClick={() => handleSubmit(prompt)}
                        color="primary"
                        disabled={isLoadingResponse}
                      >
                        <Send />
                      </SendButton>
                    </span>
                  </Tooltip>
                </div>
              </ChatRow>
            </ChatContainer>
          </StyledChatContainer>
        </div>
      </MainContainer>
    </ChatPageContainer>
  );
};

export default Chat;
